"use client";

import { formatter } from "@/app/checkout/(squeeze)/review/checkoutReviewPage";
import LegitScriptsCeritified from "@/app/checkout/legitScriptsCeritified";
import SecureCheckout from "@/app/checkout/secureCheckout";
import ShippingAndSalesTaxModal from "@/app/components/shippingAndSalesTaxModal";
import useCheckout from "@/app/hooks/useCheckout";
import { CheckoutResponse } from "@/fetch/getCheckoutResponse";
import { useAppSelector } from "@/store/hooks";
import CaretRightIcon from "public/next-public/common/icons/caret-regular-right.svg";
import React, { useCallback, useMemo, useState } from "react";
import style from "./orderSummary.module.scss";

export default function OrderSummary({
  nextStepButton,
  showNextStepButtonOnDesktop = false,
  backStepButton,
  showBackStepButtonOnDesktop = false,
  isConfirmationPage = false,
  checkoutResponse,
  stuck = true,
}: OrderSummaryProps) {
  const { nextStep, checkoutCart, getSelectedShippingAddress } = useCheckout();
  const [visibleOnMobile, setVisibleOnMobile] = useState(isConfirmationPage);
  const [showModel, setShowModel] = useState(false);
  const onToggleClick = useCallback(() => {
    setVisibleOnMobile(!visibleOnMobile);
  }, [visibleOnMobile]);
  const showModelCallback = useCallback(() => {
    setShowModel(true);
  }, []);
  const { checkoutReviewDetails } = useAppSelector((state) => state.checkoutSlice);

  const { savings, retailTotal, subTotal, totalShipping, total, estimatedTax, retailDeliveryFee, regionCode } =
    useMemo(() => {
      if (!checkoutResponse) {
        checkoutResponse = checkoutReviewDetails;
      }
      const chosenShippingAddress = getSelectedShippingAddress();
      return {
        savings: checkoutResponse?.savings || checkoutCart?.savingsOverRetail || undefined,
        retailTotal: checkoutResponse?.retailTotal || checkoutCart?.retailTotal || undefined,
        subTotal: checkoutResponse?.subTotal || checkoutCart?.subTotal || undefined,
        totalShipping:
          (checkoutResponse?.standardShipping || checkoutCart?.standardShipping || 0.0) +
          (checkoutResponse?.heavyShipping || checkoutCart?.heavyShipping || 0.0),
        total: checkoutResponse?.total || checkoutCart?.grandTotal || undefined,
        estimatedTax: checkoutResponse?.totalTax !== undefined ? checkoutResponse?.totalTax : undefined,
        retailDeliveryFee: checkoutResponse?.retailDeliveryFee || 0.0,
        regionCode: chosenShippingAddress?.RegionCD || "",
      };
    }, [checkoutResponse, checkoutCart, checkoutReviewDetails]);

  // Guard on loading so the fetch skeleton shows instead of a premature "FREE".
  const shippingIsFree = nextStep !== "loading" && totalShipping <= 0.0;

  return (
    <div
      className={`${style.OrderSummary} ${visibleOnMobile || isConfirmationPage ? style.summaryTableVisible : ""} ${stuck && !isConfirmationPage ? style.stuck : ""} ${showNextStepButtonOnDesktop ? style.hasDesktopButton : ""}`}
    >
      <div className={style.header}>
        <h2>Order Summary</h2>
        {!isConfirmationPage && (
          <button
            type="button"
            className={`${style.toggleButton} ${visibleOnMobile ? style.flip : ""}`}
            onClick={onToggleClick}
            aria-label="Toggle order summary details"
            aria-expanded={visibleOnMobile}
          >
            <CaretRightIcon className={style.toggleIcon} aria-hidden />
          </button>
        )}
      </div>
      <div className={style.summaryTable}>
        <table className="w-100">
          <tbody>
            {(nextStep === "loading" || (retailTotal && subTotal && retailTotal > subTotal)) && (
              <tr>
                <td className={style.label}>Retail Price</td>
                <td className={`${style.price} ${nextStep === "loading" ? style.loading : ""}`}>
                  {retailTotal && formatter.format(retailTotal)}
                </td>
              </tr>
            )}
            {(nextStep === "loading" || (savings ?? 0) > 0) && (
              <tr>
                <td className={`${style.label} ${style.savings}`}>Savings</td>
                <td className={`${style.savings} ${style.price}`}>
                  {(savings ?? 0) > 0 && `-${formatter.format(savings ?? 0)}`}
                </td>
              </tr>
            )}
            <tr>
              <td className={style.label}>Subtotal</td>
              <td className={`${style.price} ${nextStep === "loading" ? style.loading : ""}`}>
                {subTotal ? formatter.format(subTotal) : "$0.00"}
              </td>
            </tr>
            <tr>
              <td className={style.label}>Shipping</td>
              <td
                className={`${style.price} ${nextStep === "loading" ? style.loading : ""} ${shippingIsFree ? style.free : ""}`}
              >
                {shippingIsFree ? "FREE" : formatter.format(totalShipping)}
              </td>
            </tr>
            {(nextStep === "loading" || estimatedTax !== undefined) && (
              <tr>
                <td className={style.label}>Estimated Tax</td>
                <td className={`${style.price} ${nextStep === "loading" ? style.loading : ""}`}>
                  {estimatedTax ? formatter.format(estimatedTax) : "$0.00"}
                </td>
              </tr>
            )}
            {(retailDeliveryFee ?? 0.0) > 0.0 && (
              <tr>
                <td className={style.label}>{regionCode} Retail Delivery Fees</td>
                <td className={`${style.price} ${nextStep === "loading" ? style.loading : ""}`}>
                  {retailDeliveryFee ? formatter.format(retailDeliveryFee) : ""}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className={style.grandTotal}>
        <div className={style.grandTotalPriceAndLabel}>
          <div className={style.label}>Grand Total</div>
          <div className={`${style.price} ${nextStep === "loading" ? style.loading : ""}`}>
            {total ? formatter.format(total) : "$0.00"}
          </div>
        </div>
        <div className={style.shippingAndSalesTaxInfo}>
          <button type="button" className={style.shippingTaxLink} onClick={showModelCallback}>
            Shipping and Sales Tax <span className="d-none d-lg-inline">Info</span>
          </button>
          <ShippingAndSalesTaxModal show={showModel} onHide={() => setShowModel(false)} />
        </div>
      </div>

      <div className={style.nextStepButton}>
        {isConfirmationPage && <div className={style.grandTotalText}>Grand Total</div>}
        <div className={backStepButton ? style.orderSummaryButtonsFlex : ""}>
          {nextStep !== "loading" && !isConfirmationPage && backStepButton && (
            <div
              className={`d-flex flex-lg-column d-print-none ${showBackStepButtonOnDesktop ? "" : "d-lg-none"}  ${style.flex33}`}
            >
              {backStepButton}
              <div className={style.spacer} />
            </div>
          )}
          {nextStep !== "loading" && !isConfirmationPage && (
            <div
              className={`d-flex flex-lg-column d-print-none ${showNextStepButtonOnDesktop ? "" : "d-lg-none"}  ${style.flex66}`}
            >
              {nextStepButton}
              <div className={style.spacer} />
            </div>
          )}
        </div>
      </div>
      <div className={style.footer}>
        <SecureCheckout />
        <LegitScriptsCeritified />
      </div>
    </div>
  );
}

export type OrderSummaryProps = {
  nextStepButton: React.ReactNode;
  showNextStepButtonOnDesktop?: boolean;
  backStepButton?: React.ReactNode;
  showBackStepButtonOnDesktop?: boolean;
  isConfirmationPage?: boolean;
  checkoutResponse?: CheckoutResponse | null;
  /** Below lg the sticky footer only casts a shadow while it is floating (stuck). */
  stuck?: boolean;
};
